import React from 'react';
import { Typography, Card, Divider, Tag, Alert, Table, Tabs, Grid } from 'antd';
import {
    ToolOutlined, ApartmentOutlined, DollarOutlined, PlusOutlined,
    SwapOutlined, InfoCircleOutlined, WarningOutlined, CheckCircleOutlined,
    GlobalOutlined, MessageOutlined, LockOutlined, CodeOutlined,
    FileImageOutlined, FilePdfOutlined, MailOutlined, BarChartOutlined,
    AuditOutlined, EyeOutlined, SettingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { isSuperAdmin } from '../utils/roleUtils';

const { Title, Paragraph, Text } = Typography;

const Step = ({ number, children }) => (
    <div style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        background: '#f9f9f9',
        border: '1px solid #e8e8e8',
        borderRadius: 6,
        padding: '12px 16px',
        marginBottom: 10,
    }}>
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
            height: 28,
            borderRadius: '50%',
            background: '#5c7cfa',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.85rem',
            flexShrink: 0,
        }}>{number}</span>
        <div style={{ flex: 1, minWidth: 0, lineHeight: 1.6 }}>{children}</div>
    </div>
);

/** Responsive wrapper for card content with an icon */
const SectionCard = ({ icon, children, id }) => (
    <div id={id}>
        <Card style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {icon && <span style={{ fontSize: 22, color: '#5c7cfa', marginTop: 4, flexShrink: 0 }}>{icon}</span>}
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    {children}
                </div>
            </div>
        </Card>
    </div>
);

const HowToGuide = () => {
    const { t } = useTranslation();
    const pageLayoutProps = usePageLayout();
    const { role } = pageLayoutProps;
    const isSuperAdminRole = isSuperAdmin(role);
    const screens = Grid.useBreakpoint();
    const isMobile = screens.md === false;

    const h = (lvl) => isMobile ? Math.min(lvl + 1, 5) : lvl;

    const exampleRuleColumns = [
        { title: 'Step', dataIndex: 'step', key: 'step', width: 50 },
        { title: 'Type', dataIndex: 'type', key: 'type', width: 90 },
        { title: 'Calculation', dataIndex: 'calc', key: 'calc' },
        { title: 'Result Name', dataIndex: 'result', key: 'result', width: 110 },
    ];

    const exampleRuleData = [
        { key: 1, step: 1, type: 'Multiply', calc: 'Cost per watt \u00d7 system size in watts', result: 'Panel Cost' },
        { key: 2, step: 2, type: 'Sum', calc: 'Panel Cost + Inverter Cost + Labour Cost + DC + AC Components', result: 'Base Cost' },
        { key: 3, step: 3, type: 'Margin', calc: 'Base Cost \u00d7 margin multiplier (e.g. 1.20 for 20\u202f%)', result: 'With Margin' },
        { key: 4, step: 4, type: 'VAT', calc: 'With Margin \u00d7 VAT multiplier (e.g. 1.05 for 5\u202f%)', result: 'Final Price' },
    ];

    const formatColumns = [
        { title: 'What', dataIndex: 'what', key: 'what' },
        { title: 'Format', dataIndex: 'format', key: 'format', width: isMobile ? 80 : 140 },
        { title: 'Example', dataIndex: 'example', key: 'example', width: isMobile ? 100 : 160 },
    ];

    const formatData = [
        { key: 1, what: 'Cost per watt', format: 'Decimal (up to 10 dp)', example: '0.25 means \u00a30.25/watt' },
        { key: 2, what: 'Total fixed cost', format: 'Whole number or 2 dp', example: '600 means \u00a3600' },
        { key: 3, what: 'Percentage', format: 'Whole number', example: '20 means 20\u202f%' },
    ];

    const codeStyle = {
        background: '#1e1e2e',
        color: '#a6e3a1',
        padding: isMobile ? '12px 10px' : '16px 20px',
        borderRadius: 8,
        fontSize: isMobile ? '0.7rem' : '0.8rem',
        overflowX: 'auto',
        marginBottom: 16,
        lineHeight: 1.8,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
    };

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{
                maxWidth: 900,
                margin: '0 auto',
                padding: isMobile ? '0 4px' : 0,
                overflowX: 'hidden',
            }}>
                <Title level={h(2)}>{t('howToGuide.title')}</Title>
                <Paragraph style={{ fontSize: isMobile ? '0.875rem' : '1rem', marginBottom: 8 }}>
                    {t('howToGuide.intro')}
                </Paragraph>
                <Alert
                    type="info"
                    icon={<InfoCircleOutlined />}
                    showIcon
                    style={{ marginBottom: 32 }}
                    message={t('howToGuide.alerts.welcome')}
                />

                {/* ── TABLE OF CONTENTS ── */}
                <Card style={{ marginBottom: 24 }}>
                    <Title level={h(4)} style={{ marginTop: 0 }}>Contents</Title>
                    <ul style={{ paddingLeft: '1.2rem', lineHeight: 2.2, columns: isMobile ? 1 : 2, columnGap: 32 }}>
                        {isSuperAdminRole && <li><a href="#switching-properties">Switching Properties</a></li>}
                        <li><a href="#home-data">Home / Calculation Data</a></li>
                        <li><a href="#cost-variables">Cost Variables</a></li>
                        <li><a href="#calculation-rules">Calculation Rules</a></li>
                        <li><a href="#custom-costs">Custom Costs</a></li>
                        <li><a href="#image-manager">Image Manager</a></li>
                        <li><a href="#pdf-designer">PDF Designer</a></li>
                        <li><a href="#email-config">Email Configuration</a></li>
                        <li><a href="#dashboard">Dashboard</a></li>
                        <li><a href="#calculator-preview">Calculator Preview</a></li>
                        <li><a href="#add-user">Add New User</a></li>
                        <li><a href="#account-settings">Account Settings</a></li>
                        {isSuperAdminRole && <li><a href="#audit-log">Audit Log</a></li>}
                        <li><a href="#integration">Website Integration</a></li>
                        <li><a href="#troubleshooting">Troubleshooting</a></li>
                    </ul>
                </Card>

                {/* ── SWITCHING PROPERTIES ── */}
                {isSuperAdminRole && (
                    <SectionCard id="switching-properties" icon={<SwapOutlined />}>
                        <Title level={h(3)} style={{ marginTop: 0 }}>{t('howToGuide.sections.switchingProperties')}</Title>
                        <Paragraph>
                            As a Super Admin you have access to multiple properties. A dropdown appears in the top
                            header bar — click it to choose the property you want to manage.
                        </Paragraph>
                        <Paragraph>
                            Your selected property is remembered as you move between pages, so switching once on the Home
                            page automatically carries through to Cost Variables, Calculation Rules, and Custom Costs.
                        </Paragraph>
                    </SectionCard>
                )}

                {/* ── HOME / CALCULATION DATA ── */}
                <div id="home-data">
                    <Card style={{ marginBottom: 24 }}>
                        <Title level={h(3)} style={{ marginTop: 0 }}>{t('howToGuide.sections.homeCalculationData')}</Title>
                        <Paragraph>
                            The Home page is where you update the core pricing and rate figures used when generating solar estimates.
                            These values are specific to your property and applied to every calculation.
                        </Paragraph>
                        <Paragraph>Fields are grouped into collapsible sections:</Paragraph>
                        <ul style={{ paddingLeft: '1.5rem', lineHeight: 2 }}>
                            <li><Text strong>Unit prices</Text> — electricity unit price and export price (pence per kWh).</li>
                            <li><Text strong>Item prices</Text> — CO₂ rate and add-ons such as Eddie, Zappie, and battery storage.</li>
                            <li><Text strong>Average roof cost per watt</Text> — one installer-editable rate across roof surfaces; ground-mounted systems keep a separate rate.</li>
                            <li><Text strong>Other installation costs</Text> — inverter types and DC/AC component costs.</li>
                            <li><Text strong>Paperwork</Text> — MCS and building control fees.</li>
                            <li><Text strong>Other</Text> — panel size, min/max panel count, module price, labour, margin, scaffolding, and VAT.</li>
                        </ul>
                        <Paragraph style={{ marginTop: 12 }}>
                            After making changes, click <Text strong>Save Changes</Text>. An{' '}
                            <Tag color="orange">Unsaved changes</Tag> badge appears whenever you have
                            edits that haven't been saved.
                        </Paragraph>
                        <Alert type="success" icon={<CheckCircleOutlined />} showIcon style={{ marginTop: 12 }}
                            message={t('howToGuide.alerts.homeNumberFormats')} />
                    </Card>
                </div>

                {/* ── COST VARIABLES ── */}
                <SectionCard id="cost-variables" icon={<ToolOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>{t('howToGuide.sections.costVariables')}</Title>
                    <Paragraph>
                        Variables are the individual cost values used inside Calculation Rules — panel
                        costs, inverter prices, labour rates, and add-on items. They are organised into
                        categories (tabs).
                    </Paragraph>

                    <Divider orientation="left" plain>Variable types</Divider>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2.2, marginBottom: 16 }}>
                        <li><Tag color="green">Fixed</Tag> — a set amount, e.g. an Eddie system costs £600.</li>
                        <li><Tag color="blue">Input</Tag> — a value entered at calculation time, e.g. system size.</li>
                        <li><Tag color="orange">Calculated</Tag> — worked out from other variables using a formula.</li>
                    </ul>

                    <Divider orientation="left" plain>Adding a new variable</Divider>
                    <Step number={1}>Click <Text strong>{t('howToGuide.buttons.addNewVariable')}</Text>.</Step>
                    <Step number={2}>
                        Fill in the form:
                        <ul style={{ marginTop: 8, paddingLeft: '1.2rem' }}>
                            <li><Text strong>Variable Name</Text> — a clear label.</li>
                            <li><Text strong>System Key</Text> — a short unique identifier (<Text code>teslaCost</Text>). <Text type="danger">Cannot be changed after creation.</Text></li>
                            <li><Text strong>Type</Text> — Fixed, Input, or Calculated.</li>
                            <li><Text strong>Value or Formula</Text> — numeric amount or formula.</li>
                            <li><Text strong>Category</Text> — which tab it belongs to.</li>
                            <li><Text strong>Display Unit</Text> — optional symbol, e.g. £.</li>
                        </ul>
                    </Step>
                    <Step number={3}>Click <Text strong>{t('howToGuide.buttons.createVariable')}</Text>.</Step>
                    <Alert type="success" icon={<CheckCircleOutlined />} showIcon
                        style={{ marginTop: 4, marginBottom: 16 }}
                        message={t('howToGuide.alerts.variableNewTab')} />

                    <Divider orientation="left" plain>Editing a variable</Divider>
                    <Step number={1}>Find the variable in the table (switch tabs to find the right category).</Step>
                    <Step number={2}>Click the <Text strong>pencil icon</Text>.</Step>
                    <Step number={3}>Update the value or other fields. The System Key is locked.</Step>
                    <Step number={4}>Click <Text strong>{t('howToGuide.buttons.updateVariable')}</Text>.</Step>

                    <Divider orientation="left" plain>Deleting a variable</Divider>
                    <Alert type="warning" icon={<WarningOutlined />} showIcon style={{ marginBottom: 12 }}
                        message={t('howToGuide.alerts.deletingVariable')} />
                    <Step number={1}>Click the <Text strong>red bin icon</Text> and confirm when prompted.</Step>

                    <Divider orientation="left" plain>Resetting to defaults</Divider>
                    <Alert type="error" icon={<WarningOutlined />} showIcon style={{ marginBottom: 12 }}
                        message={t('howToGuide.alerts.resetVariables')} />
                    <Paragraph>
                        After resetting, recreate any custom variables you had previously.
                    </Paragraph>

                    <Divider orientation="left" plain>Number format reference</Divider>
                    <Table columns={formatColumns} dataSource={formatData}
                        pagination={false} size="small" scroll={{ x: 'max-content' }}
                        style={{ marginBottom: 8 }} />
                </SectionCard>

                {/* ── CALCULATION RULES ── */}
                <SectionCard id="calculation-rules" icon={<ApartmentOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>{t('howToGuide.sections.calculationRules')}</Title>
                    <Paragraph>
                        A Calculation Rule tells the system <em>how</em> to turn variables into a final installation
                        price. Think of it like a recipe: list ingredients (variables), follow steps in order,
                        then apply finishing touches (margin and VAT).
                    </Paragraph>
                    <Paragraph>
                        Only one rule can be <Tag color="green">Active</Tag> at a time.
                    </Paragraph>

                    <Divider orientation="left" plain>Step types</Divider>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2 }}>
                        <li><Text strong>Sum</Text> — adds variables together.</li>
                        <li><Text strong>Multiply</Text> — multiplies one variable by a fixed number.</li>
                    </ul>
                    <Paragraph>
                        Each step produces an <Text strong>Output Variable</Text> that later steps can reference.
                    </Paragraph>

                    <Divider orientation="left" plain>Creating a rule</Divider>
                    <Step number={1}>Click <Text strong>{t('howToGuide.buttons.createNewRule')}</Text>.</Step>
                    <Step number={2}>Enter a <Text strong>Rule Name</Text> and optional description.</Step>
                    <Step number={3}>Add steps with <Text strong>{t('howToGuide.buttons.addSumStep')}</Text> or <Text strong>{t('howToGuide.buttons.addMultiplyStep')}</Text>.</Step>
                    <Step number={4}>
                        Configure each step:
                        <ul style={{ marginTop: 8, paddingLeft: '1.2rem' }}>
                            <li>Give it a meaningful name.</li>
                            <li>For Sum: select variables to add.</li>
                            <li>For Multiply: select the base and enter the multiplier.</li>
                            <li>Set the <Text strong>Output Variable</Text> name.</li>
                        </ul>
                    </Step>
                    <Step number={5}>Set <Text strong>Margin %</Text> and <Text strong>VAT %</Text> in Calculation Phases.</Step>
                    <Step number={6}>Click <Text strong>{t('howToGuide.buttons.createRule')}</Text>.</Step>

                    <Divider orientation="left" plain>Example rule</Divider>
                    <Table columns={exampleRuleColumns} dataSource={exampleRuleData}
                        pagination={false} size="small" scroll={{ x: 'max-content' }}
                        style={{ marginBottom: 16 }} />

                    <Divider orientation="left" plain>Activating a rule</Divider>
                    <Step number={1}>Click <Text strong>{t('howToGuide.buttons.activate')}</Text> on the rule's row. It is immediately used for all new calculations.</Step>
                    <Alert type="success" icon={<CheckCircleOutlined />} showIcon
                        style={{ marginTop: 4, marginBottom: 16 }}
                        message={t('howToGuide.alerts.ruleTestTip')} />

                    <Divider orientation="left" plain>Deleting a rule</Divider>
                    <Alert type="warning" icon={<WarningOutlined />} showIcon style={{ marginBottom: 12 }}
                        message={t('howToGuide.alerts.ruleDeleteWarning')} />
                    <Step number={1}>Click the <Text strong>red bin icon</Text> and confirm. This cannot be undone.</Step>
                </SectionCard>

                {/* ── CUSTOM COSTS ── */}
                <SectionCard id="custom-costs" icon={<DollarOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>{t('howToGuide.sections.customCosts')}</Title>
                    <Paragraph>
                        Custom Costs let you add one-off or property-specific line items outside the standard
                        variable/rule system — for example a unique survey fee, specialist access charge,
                        or a temporary promotion discount.
                    </Paragraph>
                    <Paragraph>
                        Each custom cost has a name, description, and value. They appear as separate line items
                        in the customer-facing output so the pricing remains transparent.
                    </Paragraph>
                </SectionCard>

                {/* ── IMAGE MANAGER ── */}
                <SectionCard id="image-manager" icon={<FileImageOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>Image Manager</Title>
                    <Paragraph>
                        Upload and manage images used throughout the system — your company logo,
                        PDF header banner, and up to five additional page images for the PDF designer.
                    </Paragraph>

                    <Divider orientation="left" plain>Available image slots</Divider>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2 }}>
                        <li><Text strong>Company Logo</Text> — displayed on PDFs and email headers.</li>
                        <li><Text strong>PDF Header</Text> — a wide banner for the top of PDF pages.</li>
                        <li><Text strong>Page Images 1–5</Text> — drag onto any PDF page in the designer.</li>
                    </ul>

                    <Divider orientation="left" plain>Uploading an image</Divider>
                    <Step number={1}>Click the upload area for the slot you want to update.</Step>
                    <Step number={2}>Select a file — JPG, PNG, or WebP.</Step>
                    <Step number={3}>The image uploads immediately. A preview appears once processed.</Step>
                    <Alert type="info" icon={<InfoCircleOutlined />} showIcon style={{ marginTop: 12 }}
                        message="Keep images under 2 MB for the best performance. Large images may slow down PDF generation." />
                </SectionCard>

                {/* ── PDF DESIGNER ── */}
                <SectionCard id="pdf-designer" icon={<FilePdfOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>PDF Designer</Title>
                    <Paragraph>
                        A visual drag-and-drop editor for building the customer quote PDF. Each page is a
                        separate section tab. Place text, images, tables, icons, signature blocks, and variable
                        tables anywhere on the canvas — the final PDF matches what you see on screen.
                    </Paragraph>

                    <Divider orientation="left" plain>Canvas basics</Divider>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2.2 }}>
                        <li><Text strong>Click</Text> an element to select it (blue bounding box appears).</li>
                        <li><Text strong>Drag</Text> to move. Drag corner handles to resize.</li>
                        <li><Text strong>Double-click</Text> a text element to edit its content inline.</li>
                        <li><Text strong>Delete</Text> — select an element and click Delete in the properties panel.</li>
                    </ul>

                    <Divider orientation="left" plain>Adding elements</Divider>
                    <Paragraph>Drag from the <Text strong>Elements</Text> toolbar on the left onto the canvas:</Paragraph>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2.2 }}>
                        <li><Text strong>Text</Text> — editable text blocks for headings, paragraphs, and labels.</li>
                        <li><Text strong>Variables</Text> — dynamic placeholders (e.g. <Text code>{'{{systemSize}}'}</Text>) replaced with real data in the final PDF.</li>
                        <li><Text strong>Images</Text> — drag an uploaded image from the palette.</li>
                        <li><Text strong>Tables</Text> — data tables with customisable rows, columns, and colours.</li>
                        <li><Text strong>Variable Tables</Text> — auto-generated tables pulling in cost variable data.</li>
                        <li><Text strong>Lines</Text> — horizontal dividers.</li>
                        <li><Text strong>Rectangles</Text> — coloured shapes for backgrounds and borders.</li>
                        <li><Text strong>Icons</Text> — solar, business, and decorative icons with customisable colour.</li>
                        <li><Text strong>Signature Blocks</Text> — pre-formatted signature, printed name, and date lines.</li>
                    </ul>

                    <Divider orientation="left" plain>Working with tables</Divider>
                    <Paragraph>When you select a table on the canvas, the properties panel shows all table settings.</Paragraph>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2.2 }}>
                        <li><Text strong>Cell content</Text> — edit text directly in the properties panel grid.</li>
                        <li><Text strong>Rows &amp; columns</Text> — use +Row, -Row, +Col, -Col buttons.</li>
                        <li><Text strong>Column widths</Text> — <Text strong>drag the blue dashed handles</Text> that appear between columns on the canvas to resize visually. Or type exact percentages in the properties panel.</li>
                        <li><Text strong>Colours</Text> — customise header background, header text, cell text, and border colours.</li>
                        <li><Text strong>Header row toggle</Text> — turn styling on or off.</li>
                    </ul>
                    <Alert type="info" icon={<InfoCircleOutlined />} showIcon
                        style={{ marginTop: 8, marginBottom: 16 }}
                        message="Table changes are buffered — edit cells and widths, then click 'Apply Changes' to push them to the canvas. This prevents accidental changes while typing." />

                    <Divider orientation="left" plain>Properties panel</Divider>
                    <Paragraph>When an element is selected, the right-hand panel shows editable properties:</Paragraph>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2.2 }}>
                        <li><Text strong>Text</Text> — font size, font family, bold, italic, colour, alignment.</li>
                        <li><Text strong>Lines</Text> — stroke colour and thickness.</li>
                        <li><Text strong>Rectangles</Text> — fill colour, border colour, border width.</li>
                        <li><Text strong>Icons</Text> — icon picker, colour, size.</li>
                        <li><Text strong>Signatures</Text> — label text, line colour, font size.</li>
                        <li><Text strong>Variable tables</Text> — variable selection, header/cell colours.</li>
                    </ul>
                    <Paragraph>
                        All numeric inputs (font size, thickness, etc.) are buffered — type your value and
                        press <Text strong>Enter</Text> or click away to apply.
                    </Paragraph>

                    <Divider orientation="left" plain>Page management</Divider>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2.2 }}>
                        <li><Text strong>Section tabs</Text> — click tabs to switch pages (Cover, Proposal, Costs, Cancellations, etc.).</li>
                        <li><Text strong>Background image</Text> — set a background via the toolbar dropdown.</li>
                        <li><Text strong>Load Template</Text> — replaces the page with the system default template.</li>
                        <li><Text strong>Auto-content toggle</Text> — when enabled, the system generates content automatically. When disabled, only canvas elements are used.</li>
                    </ul>

                    <Divider orientation="left" plain>Saving</Divider>
                    <Step number={1}>Click <Text strong>Save</Text> to store your canvas layout.</Step>
                    <Step number={2}>Click <Text strong>Save &amp; Preview</Text> to save and download a preview PDF.</Step>
                    <Alert type="warning" icon={<WarningOutlined />} showIcon style={{ marginTop: 8 }}
                        message="Always click Save after making changes. Navigating away without saving will lose your work." />
                </SectionCard>

                {/* ── EMAIL CONFIGURATION ── */}
                <SectionCard id="email-config" icon={<MailOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>Email Configuration</Title>
                    <Paragraph>
                        Controls how the system sends quote emails to customers.
                    </Paragraph>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2.2 }}>
                        <li><Text strong>From email address</Text> — the address in the "From" field. Should be a verified sender.</li>
                        <li><Text strong>Company copy</Text> — every customer quote email is automatically copied to your company address.</li>
                        <li><Text strong>Email fallback</Text> — if the email fails, the customer is offered a direct PDF download instead, ensuring they always receive their quote.</li>
                    </ul>
                    <Alert type="info" icon={<InfoCircleOutlined />} showIcon style={{ marginTop: 12 }}
                        message="The 'From' address must be verified with the email provider (SMTP2Go). If it isn't verified, the system falls back to the default no-reply address. Contact the Solar Calc team to verify a new sender." />
                </SectionCard>

                {/* ── DASHBOARD ── */}
                <SectionCard id="dashboard" icon={<BarChartOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>Dashboard</Title>
                    <Paragraph>
                        The Dashboard provides an overview of account activity — quotes generated, recent activity,
                        and usage statistics. It's the quickest way to see how your calculator is performing.
                    </Paragraph>
                </SectionCard>

                {/* ── CALCULATOR PREVIEW ── */}
                <SectionCard id="calculator-preview" icon={<EyeOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>Calculator Preview</Title>
                    <Paragraph>
                        Test your calculator exactly as customers will see it. Use it after changing variables,
                        rules, or costs to verify the output before any customers see it.
                    </Paragraph>
                    <Step number={1}>Navigate to Calculator Preview from the sidebar.</Step>
                    <Step number={2}>Fill in a test set of values (roof type, system size, etc.).</Step>
                    <Step number={3}>Submit and review the results — check the pricing breakdown matches your expectations.</Step>
                </SectionCard>

                {/* ── ADD NEW USER ── */}
                <SectionCard id="add-user" icon={<PlusOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>{t('howToGuide.sections.addingNewUser')}</Title>
                    <Paragraph>
                        Admin and Super Admin accounts can create additional users via{' '}
                        <Text strong>{t('howToGuide.buttons.addNewUser')}</Text> in the sidebar.
                    </Paragraph>
                    <Step number={1}>Enter the user's first name, last name, and email address.</Step>
                    <Step number={2}>Select the property they should be assigned to.</Step>
                    <Step number={3}>
                        Choose their role:
                        <ul style={{ marginTop: 8, paddingLeft: '1.2rem' }}>
                            <li><Text strong>Admin</Text> — full access to variables, rules, custom costs, and calculation data.</li>
                            <li><Text strong>User</Text> — can update the Home page values for their property only.</li>
                        </ul>
                    </Step>
                    <Step number={4}>Click <Text strong>{t('howToGuide.buttons.createUser')}</Text>. A temporary password is sent by email.</Step>
                    <Paragraph style={{ marginTop: 8 }}>
                        On first login the new user will be prompted to set their own password.
                    </Paragraph>
                </SectionCard>

                {/* ── ACCOUNT SETTINGS ── */}
                <SectionCard id="account-settings" icon={<SettingOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>Account Settings</Title>
                    <Paragraph>Manage your own login credentials and security:</Paragraph>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2.2 }}>
                        <li><Text strong>Change password</Text> — update your login password at any time.</li>
                        <li><Text strong>Two-factor authentication (2FA)</Text> — enable TOTP-based 2FA for extra security using an authenticator app.</li>
                    </ul>
                </SectionCard>

                {/* ── AUDIT LOG ── */}
                {isSuperAdminRole && (
                    <SectionCard id="audit-log" icon={<AuditOutlined />}>
                        <Title level={h(3)} style={{ marginTop: 0 }}>Audit Log</Title>
                        <Paragraph>
                            Records every significant action — who changed what, and when. Use it to track changes
                            across all users and properties, or to investigate modifications.
                        </Paragraph>
                        <Paragraph>
                            Entries are displayed most recent first. Filter by property or search for specific actions.
                        </Paragraph>
                    </SectionCard>
                )}

                {/* ── INTEGRATION GUIDE ── */}
                <SectionCard id="integration" icon={<CodeOutlined />}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>{t('howToGuide.sections.websiteIntegration')}</Title>
                    <Paragraph>
                        Follow the steps below to add your calculator to your website.
                        Select the product you have, then choose your platform for specific instructions.
                    </Paragraph>
                    <Alert type="info" icon={<InfoCircleOutlined />} showIcon style={{ marginBottom: 24 }}
                        message={t('howToGuide.alerts.embedPlaceholder')} />

                    <Tabs
                        defaultActiveKey="embed"
                        size={isMobile ? 'small' : 'middle'}
                        items={[
                            {
                                key: 'embed',
                                label: <span><GlobalOutlined style={{ marginRight: 6 }} />{t('howToGuide.platforms.fullPageEmbed')}</span>,
                                children: (
                                    <div>
                                        <Paragraph>
                                            The calculator fills a full section or page of your website via an iframe.
                                        </Paragraph>
                                        <Divider orientation="left" plain>Your embed code</Divider>
                                        <pre style={codeStyle}>{`<iframe\n  src="https://calc.solar-calc.co.uk/?property=YOUR_ID"\n  width="100%"\n  height="700"\n  frameborder="0"\n  style="border:none;"\n></iframe>`}</pre>
                                        <Alert type="success" showIcon icon={<CheckCircleOutlined />} style={{ marginBottom: 20 }}
                                            message={t('howToGuide.alerts.embedHeight')} />
                                        <Tabs
                                            size="small"
                                            items={[
                                                {
                                                    key: 'wix', label: 'Wix', children: (
                                                        <div>
                                                            <Step number={1}>Log in to Wix and open your site editor.</Step>
                                                            <Step number={2}>Click <Text strong>+</Text> → <Text strong>Embed</Text> → <Text strong>Embed a Widget</Text> → <Text strong>HTML iFrame</Text>.</Step>
                                                            <Step number={3}>Click <Text strong>Enter Code</Text> and paste your embed code.</Step>
                                                            <Step number={4}>Drag the corners to fill the page. Set height to at least 700px.</Step>
                                                            <Step number={5}>Click <Text strong>Publish</Text>.</Step>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    key: 'wp', label: t('howToGuide.platforms.wordpress'), children: (
                                                        <div>
                                                            <Step number={1}>Open (or create) the target page in WordPress.</Step>
                                                            <Step number={2}>Click <Text strong>+</Text> → search for <Text strong>Custom HTML</Text>.</Step>
                                                            <Step number={3}>Paste your embed code.</Step>
                                                            <Step number={4}><Text strong>Preview</Text> then <Text strong>Publish</Text>.</Step>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    key: 'sq', label: t('howToGuide.platforms.squarespace'), children: (
                                                        <div>
                                                            <Step number={1}>Open the page and click <Text strong>Edit</Text>.</Step>
                                                            <Step number={2}>Click <Text strong>+</Text> → <Text strong>Code</Text>.</Step>
                                                            <Step number={3}>Select <Text strong>HTML</Text> and paste your embed code.</Step>
                                                            <Step number={4}>Click <Text strong>Apply</Text> then <Text strong>Save</Text>.</Step>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    key: 'other', label: t('howToGuide.platforms.other'), children: (
                                                        <div>
                                                            <Step number={1}>Look for <Text strong>Edit HTML</Text>, <Text strong>Embed code</Text>, or <Text strong>HTML element</Text> in your builder.</Step>
                                                            <Step number={2}>Paste your embed code where you want the calculator.</Step>
                                                            <Step number={3}>Save and preview.</Step>
                                                        </div>
                                                    )
                                                },
                                            ]}
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'widget',
                                label: <span><MessageOutlined style={{ marginRight: 6 }} />Floating Widget</span>,
                                children: (
                                    <div>
                                        <Paragraph>
                                            A quote button floats in the corner of every page. Add the code once to your footer.
                                        </Paragraph>
                                        <Divider orientation="left" plain>Your widget code</Divider>
                                        <pre style={codeStyle}>{`<script\n  src="https://widget.solar-calc.co.uk/embed.js"\n  data-property="YOUR_ID"\n  defer>\n</script>`}</pre>
                                        <Alert type="success" showIcon icon={<CheckCircleOutlined />} style={{ marginBottom: 20 }}
                                            message="Tip: add this code once in your site's footer and the widget appears on every page." />
                                        <Tabs
                                            size="small"
                                            items={[
                                                {
                                                    key: 'wix', label: 'Wix', children: (
                                                        <div>
                                                            <Step number={1}>Open your Wix editor.</Step>
                                                            <Step number={2}>Click <Text strong>+</Text> → <Text strong>Embed</Text> → <Text strong>HTML iFrame</Text>.</Step>
                                                            <Step number={3}>Paste your widget code.</Step>
                                                            <Step number={4}>Right-click → <Text strong>Show on All Pages</Text>.</Step>
                                                            <Step number={5}><Text strong>Publish</Text>.</Step>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    key: 'wp', label: 'WordPress', children: (
                                                        <div>
                                                            <Step number={1}>Go to <Text strong>Appearance → Theme File Editor</Text> → <Text code>footer.php</Text>.</Step>
                                                            <Step number={2}>Paste your widget code just before <Text code>{'</body>'}</Text>.</Step>
                                                            <Step number={3}>Click <Text strong>Update File</Text>.</Step>
                                                            <Alert type="info" showIcon style={{ marginTop: 8 }}
                                                                message='Prefer not to edit theme files? Use the "Insert Headers and Footers" plugin instead.' />
                                                        </div>
                                                    )
                                                },
                                                {
                                                    key: 'sq', label: 'Squarespace', children: (
                                                        <div>
                                                            <Step number={1}>Go to <Text strong>Settings → Advanced → Code Injection</Text>.</Step>
                                                            <Step number={2}>Paste into the <Text strong>Footer</Text> box.</Step>
                                                            <Step number={3}><Text strong>Save</Text>.</Step>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    key: 'other', label: 'Other', children: (
                                                        <div>
                                                            <Step number={1}>Look for <Text strong>Footer Code</Text> or <Text strong>Code Injection</Text> in your platform settings.</Step>
                                                            <Step number={2}>Paste your widget code.</Step>
                                                            <Step number={3}>Save and check your live site.</Step>
                                                        </div>
                                                    )
                                                },
                                            ]}
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'internal',
                                label: <span><LockOutlined style={{ marginRight: 6 }} />Internal Tool</span>,
                                children: (
                                    <div>
                                        <Paragraph>
                                            Your internal tool is a private page — no embedding needed. Access it via a private URL.
                                        </Paragraph>
                                        <Divider orientation="left" plain>Your private URL</Divider>
                                        <pre style={codeStyle}>{`https://calc.solar-calc.co.uk/internal/YOUR_ID`}</pre>
                                        <Step number={1}><Text strong>Bookmark the URL</Text> on every device your team uses.</Step>
                                        <Step number={2}><Text strong>Share it</Text> — anyone with the link can use the tool.</Step>
                                        <Step number={3}><Text strong>Optional:</Text> add a home screen shortcut on mobile for quick access during site visits.</Step>
                                        <Alert type="success" showIcon icon={<CheckCircleOutlined />} style={{ marginTop: 16 }}
                                            message="If your plan includes the floating widget, you'll also receive a separate widget code for customer-facing use." />
                                    </div>
                                ),
                            },
                        ]}
                    />
                </SectionCard>

                {/* ── TROUBLESHOOTING ── */}
                <div id="troubleshooting">
                    <Card style={{ marginBottom: 24 }}>
                        <Title level={h(3)} style={{ marginTop: 0 }}>Troubleshooting &amp; Common Questions</Title>

                        <Divider orientation="left" plain>Common issues</Divider>

                        <div style={{ marginBottom: 16 }}>
                            <Paragraph><Text type="danger" strong>I added a variable but it doesn't appear.</Text></Paragraph>
                            <ul style={{ paddingLeft: '1.5rem', lineHeight: 2 }}>
                                <li>Check the category tab — it may be in a different tab.</li>
                                <li>Make sure you clicked <Text strong>Create Variable</Text>.</li>
                                <li>Try refreshing the page.</li>
                            </ul>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Paragraph><Text type="danger" strong>Unexpected prices in the output.</Text></Paragraph>
                            <ul style={{ paddingLeft: '1.5rem', lineHeight: 2 }}>
                                <li>Confirm your rule is <Tag color="green">Active</Tag>.</li>
                                <li>Check steps are in the right order.</li>
                                <li>Verify variable values and margin/VAT percentages.</li>
                            </ul>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Paragraph><Text type="danger" strong>Customer didn't receive their quote email.</Text></Paragraph>
                            <ul style={{ paddingLeft: '1.5rem', lineHeight: 2 }}>
                                <li>If the server is down, customers see a "service unavailable" message.</li>
                                <li>If email fails, the system offers a direct PDF download automatically.</li>
                                <li>Check your company copy email — a copy is always sent there.</li>
                                <li>If emails consistently go to spam, verify your "From" address with the email provider.</li>
                            </ul>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Paragraph><Text type="danger" strong>PDF changes didn't save.</Text></Paragraph>
                            <ul style={{ paddingLeft: '1.5rem', lineHeight: 2 }}>
                                <li>Click <Text strong>Save</Text> or <Text strong>Save &amp; Preview</Text> in the PDF Designer — changes are not auto-saved.</li>
                                <li>For table edits, click <Text strong>Apply Changes</Text> first, then Save.</li>
                            </ul>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Paragraph><Text type="danger" strong>Accidentally deleted a variable.</Text></Paragraph>
                            <Paragraph>
                                Use <Text strong>Reset All Variables</Text> to reload defaults, then recreate custom variables.
                            </Paragraph>
                        </div>

                        <Divider orientation="left" plain>General questions</Divider>

                        <div style={{ marginBottom: 12 }}>
                            <Paragraph><Text strong>Q: Can I have multiple rules active?</Text></Paragraph>
                            <Paragraph>No — only one at a time. Save as many as you like and switch between them.</Paragraph>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <Paragraph><Text strong>Q: Will changing a rule affect quotes already sent?</Text></Paragraph>
                            <Paragraph>No. Only new quotes use the updated rule.</Paragraph>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <Paragraph><Text strong>Q: How do I test a rule before going live?</Text></Paragraph>
                            <Paragraph>
                                Create a test rule, activate it, and use Calculator Preview to check the numbers.
                            </Paragraph>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <Paragraph><Text strong>Q: How do I resize table columns?</Text></Paragraph>
                            <Paragraph>
                                Select the table in the PDF Designer — blue dashed handles appear between columns.
                                Drag them to resize. You can also type exact percentages in the properties panel.
                            </Paragraph>
                        </div>
                    </Card>
                </div>

                {/* ── FINAL TIPS ── */}
                <Card style={{ marginBottom: 32 }}>
                    <Title level={h(3)} style={{ marginTop: 0 }}>Final Tips</Title>
                    <ul style={{ paddingLeft: '1.5rem', lineHeight: 2.2 }}>
                        <li><Text strong>Start simple.</Text> Build a basic rule first, then add complexity.</li>
                        <li><Text strong>Test before going live.</Text> Use Calculator Preview after any pricing change.</li>
                        <li><Text strong>Use clear names.</Text> Name variables and rules so anyone knows what they do.</li>
                        <li><Text strong>Change one thing at a time</Text> when debugging.</li>
                        <li><Text strong>Number formats matter.</Text> Up to 10 dp for per-watt costs. Never include £ signs or commas.</li>
                        <li><Text strong>Keep a backup rule.</Text> Don't delete your last working rule until the new one is verified.</li>
                        <li><Text strong>Save your PDF work.</Text> Always click Save — changes are not saved automatically.</li>
                    </ul>
                </Card>
            </div>
        </PageLayout>
    );
};

export default HowToGuide;
